import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bug, LogOut, UserPen } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@org/clients/school';
import { useTranslation } from 'react-i18next';

import { Button } from '../core/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../core/dialog';
import { TextArea } from '../core/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../core/dropdown-menu';
import { SidebarTrigger } from '../core/sidebar';
import { useAuth } from '../login/auth-context';

const MAX_CAPTURED = 50;

export interface TopNavbarProps {
  eyebrowText: string;
  displayName: string;
  bgClass?: string;
  centerElements?: React.ReactNode;
  extraElements?: React.ReactNode;
  user: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    picture?: string | null;
    given_name?: string | null;
    nickname?: string | null;
    name?: string | null;
  } | null | undefined;
  app: 'admin' | 'p2e' | 'teacher' | 'community';
  logoutLabel?: string;
  loggingOutLabel?: string;
  editProfileHref?: string;
  editProfileLabel?: string;
}

interface CapturedEntry {
  type: 'error' | 'unhandled-rejection' | 'console-error' | 'network';
  message: string;
  timestamp: string;
}

function pushEntry(buf: React.MutableRefObject<CapturedEntry[]>, entry: CapturedEntry) {
  buf.current = [...buf.current.slice(-(MAX_CAPTURED - 1)), entry];
}

function now() {
  return new Date().toISOString();
}

function ReportIssueButton({
  app,
  errorsRef,
}: {
  app: 'admin' | 'p2e' | 'teacher' | 'community';
  errorsRef: React.MutableRefObject<CapturedEntry[]>;
}) {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');

  const createMutation = trpc.issueReports.create.useMutation({
    onSuccess: () => {
      setOpen(false);
      setDescription('');
      toast(t('reportIssue.successTitle'));
    },
    onError: () => {
      toast(t('reportIssue.errorTitle'), {
        description: t('reportIssue.errorDescription'),
      });
    },
  });

  const handleSubmit = useCallback(() => {
    if (!description.trim()) return;
    // Read errors at submit time, not render time
    const errors = errorsRef.current;
    const formatted =
      errors.length > 0
        ? errors.map((e) => `[${e.timestamp}] (${e.type}) ${e.message}`).join('\n')
        : undefined;

    createMutation.mutate({
      description: description.trim(),
      currentUrl: window.location.href,
      userAgent: navigator.userAgent,
      consoleErrors: formatted,
      app,
    });
  }, [description, errorsRef, app, createMutation]);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900"
        onClick={() => setOpen(true)}
        aria-label={t('reportIssue.button')}
      >
        <Bug className="h-4 w-4" />
        <span className="hidden sm:inline" aria-hidden="true">{t('reportIssue.button')}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('reportIssue.title')}</DialogTitle>
            <DialogDescription>
              {t('reportIssue.description')}
            </DialogDescription>
          </DialogHeader>

          <TextArea
            placeholder={t('reportIssue.placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="resize-none"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={createMutation.isPending}>
              {t('reportIssue.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!description.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? t('common.status.submitting') : t('reportIssue.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UserMenu({
  user,
  logoutLabel,
  loggingOutLabel,
  editProfileHref,
  editProfileLabel,
}: {
  user: TopNavbarProps['user'];
  logoutLabel?: string;
  loggingOutLabel?: string;
  editProfileHref?: string;
  editProfileLabel?: string;
}) {
  const { logout, isLoading } = useAuth();
  const { t } = useTranslation('admin');
  const effectiveLogoutLabel = logoutLabel ?? t('common.nav.signOut');
  const effectiveLoggingOutLabel = loggingOutLabel ?? t('common.nav.signingOut');

  const displayName =
    (user as { firstName?: string | null; lastName?: string | null } | null | undefined)?.firstName
      ? [
          (user as { firstName?: string | null })?.firstName,
          (user as { lastName?: string | null })?.lastName,
        ]
          .filter(Boolean)
          .join(' ')
      : user?.given_name ??
        user?.nickname ??
        user?.name ??
        user?.email ??
        'User';

  const displayEmail = user?.email ?? '';
  const picture = user?.picture;
  const initial = (displayName || displayEmail || 'U').slice(0, 1).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 shrink-0 rounded-full border border-rose-100 bg-white/80 text-[#cf2e2e] shadow-sm hover:bg-white"
        >
          {picture ? (
            <img src={picture} alt={displayName} className="h-full w-full rounded-full object-cover" />
          ) : (
            <span className="text-base font-semibold">{initial}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 space-y-1 bg-white/95">
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-900">{displayName}</span>
          <span className="text-xs text-gray-500">{displayEmail}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {editProfileHref && (
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2"
            onSelect={() => {
              window.location.href = editProfileHref;
            }}
          >
            <UserPen className="h-4 w-4" />
            {editProfileLabel ?? t('common.nav.editProfile')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="flex cursor-pointer items-center gap-2 text-[#cf2e2e]"
          onSelect={(event) => {
            event.preventDefault();
            void logout();
          }}
        >
          <LogOut className="h-4 w-4" />
          {isLoading ? effectiveLoggingOutLabel : effectiveLogoutLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TopNavbar({
  eyebrowText,
  displayName,
  bgClass = 'bg-white/80',
  centerElements,
  extraElements,
  user,
  app,
  logoutLabel,
  loggingOutLabel,
  editProfileHref,
  editProfileLabel,
}: TopNavbarProps) {
  const capturedRef = useRef<CapturedEntry[]>([]);

  useEffect(() => {
    const buf = capturedRef;

    // 1. Uncaught JS errors (syntax errors, reference errors, etc.)
    function onError(event: ErrorEvent) {
      pushEntry(buf, {
        type: 'error',
        message: `${event.message} at ${event.filename ?? '?'}:${event.lineno ?? '?'}:${event.colno ?? '?'}`,
        timestamp: now(),
      });
    }

    // 2. Unhandled promise rejections (failed API calls, async errors)
    function onRejection(event: PromiseRejectionEvent) {
      const reason = event.reason;
      let message: string;
      if (reason instanceof Error) {
        message = `${reason.message}${reason.stack ? '\n' + reason.stack : ''}`;
      } else if (typeof reason === 'string') {
        message = reason;
      } else {
        try {
          message = JSON.stringify(reason);
        } catch {
          message = String(reason);
        }
      }
      pushEntry(buf, { type: 'unhandled-rejection', message, timestamp: now() });
    }

    // 3. console.error monkey-patch
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      const message = args
        .map((a) => {
          if (typeof a === 'string') return a;
          if (a instanceof Error) return `${a.message}${a.stack ? '\n' + a.stack : ''}`;
          try { return JSON.stringify(a); } catch { return String(a); }
        })
        .join(' ');
      pushEntry(buf, { type: 'console-error', message, timestamp: now() });
      originalConsoleError(...args);
    };

    // 4. Intercept fetch to catch network errors and HTTP 4xx/5xx
    const originalFetch = window.fetch;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      try {
        const response = await originalFetch(...args);
        if (!response.ok) {
          const url = typeof args[0] === 'string' ? args[0] : args[0] instanceof Request ? args[0].url : String(args[0]);
          pushEntry(buf, {
            type: 'network',
            message: `HTTP ${response.status} ${response.statusText} - ${url}`,
            timestamp: now(),
          });
        }
        return response;
      } catch (err) {
        const url = typeof args[0] === 'string' ? args[0] : args[0] instanceof Request ? args[0].url : String(args[0]);
        const msg = err instanceof Error ? err.message : String(err);
        pushEntry(buf, {
          type: 'network',
          message: `Network error: ${msg} - ${url}`,
          timestamp: now(),
        });
        throw err;
      }
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      console.error = originalConsoleError;
      window.fetch = originalFetch;
    };
  }, []);

  return (
    <header
      className={`sticky top-0 z-10 flex items-center gap-4 border-b border-rose-100 ${bgClass} px-4 py-4 backdrop-blur sm:px-6 lg:px-8`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <SidebarTrigger className="shrink-0 text-[#cf2e2e]" />
        <div className="min-w-0">
          <p className="truncate text-xs uppercase tracking-[0.3em] text-[#cf2e2e]">{eyebrowText}</p>
          <h2 className="truncate text-2xl font-semibold text-gray-900">{displayName}</h2>
        </div>
      </div>

      {centerElements && (
        <div className="flex min-w-0 flex-1 items-center justify-center">
          {centerElements}
        </div>
      )}

      <div className={`flex min-w-0 items-center justify-end gap-3 ${centerElements ? '' : 'ml-auto'}`}>
        {extraElements}
        <ReportIssueButton app={app} errorsRef={capturedRef} />
        <UserMenu user={user} logoutLabel={logoutLabel} loggingOutLabel={loggingOutLabel} editProfileHref={editProfileHref} editProfileLabel={editProfileLabel} />
      </div>
    </header>
  );
}
